import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { CustomFieldTypeName } from '@air/api/types';
import {
  contentCollectionClipsSelector,
  contentCollectionCustomFieldsSelector,
  contentCollectionFormSelector,
  setCustomFieldDateValueAction,
  setCustomFieldPlainTextValueAction,
  setCustomFieldValueAction,
  setCustomFieldValuesAction,
  updateContentCollectionCustomFieldsInAllClipsAction,
} from '@air/feature-content-collection/redux';
import {
  ContentCollectionSidebar,
  ContentCollectionSidebarCustomFields,
  type ContentCollectionSidebarCustomFieldsProps,
  ContentCollectionSidebarHeader,
  ContentCollectionSidebarInstructions,
  ContentCollectionSidebarSeparator,
  ContentCollectionSidebarTerms,
} from '@air/feature-content-collection/ui';
import { useBreakpointsContext } from '@air/provider-media-query';

import { ContentCollectionCustomFieldDateInput } from '~/components/ContentCollection/ContentCollectionCustomFieldDateInput';
import { ContentCollectionCustomFieldMultiSelect } from '~/components/ContentCollection/ContentCollectionCustomFieldMultiSelect';
import { ContentCollectionCustomFieldPlainText } from '~/components/ContentCollection/ContentCollectionCustomFieldPlainText';
import { ContentCollectionCustomFieldSingleSelect } from '~/components/ContentCollection/ContentCollectionCustomFieldSingleSelect';
import { Routes } from '~/constants/routes';

export const ContentCollectionManageViewSidebar = () => {
  const { isAboveMediumScreen } = useBreakpointsContext();
  const dispatch = useDispatch();
  const form = useSelector(contentCollectionFormSelector);
  const clips = useSelector(contentCollectionClipsSelector);
  const customFields = useSelector(contentCollectionCustomFieldsSelector);

  const showCustomFields = form.fields.some((field) => field.type === 'custom_field');

  const renderSidebarCustomField = useCallback<ContentCollectionSidebarCustomFieldsProps['renderCustomField']>(
    ({ customField, disabled }) => {
      const sharedProps = {
        clips,
        customField,
        disabled: disabled,
        showDescription: true,
      };

      switch (customField.type.title) {
        case CustomFieldTypeName.singleSelect: {
          return (
            <ContentCollectionCustomFieldSingleSelect
              onSuccess={({ customFieldWithValues, values }) => {
                dispatch(
                  updateContentCollectionCustomFieldsInAllClipsAction({
                    customField: { ...customFieldWithValues, value: values ? values[0] : null },
                  }),
                );

                dispatch(
                  setCustomFieldValueAction({
                    customField: { ...customFieldWithValues, value: values ? values[0] : null },
                  }),
                );
              }}
              value={customFields.find(({ id }) => id === customField.id)?.value}
              {...sharedProps}
            />
          );
        }
        case CustomFieldTypeName.multiSelect: {
          return (
            <ContentCollectionCustomFieldMultiSelect
              onSuccess={({ customFieldWithValues, values }) => {
                dispatch(
                  updateContentCollectionCustomFieldsInAllClipsAction({
                    customField: { ...customFieldWithValues, values: values ?? null },
                  }),
                );

                dispatch(
                  setCustomFieldValuesAction({
                    customField: { ...customFieldWithValues, values: values ?? null },
                  }),
                );
              }}
              values={customFields.find(({ id }) => id === customField.id)?.values}
              {...sharedProps}
            />
          );
        }
        case CustomFieldTypeName.plainText: {
          return (
            <ContentCollectionCustomFieldPlainText
              onSuccess={({ customFieldWithValues, value }) => {
                dispatch(
                  updateContentCollectionCustomFieldsInAllClipsAction({
                    customField: {
                      ...customFieldWithValues,
                      plainTextValue: value,
                    },
                  }),
                );

                dispatch(
                  setCustomFieldPlainTextValueAction({
                    customField: {
                      ...customFieldWithValues,
                      plainTextValue: value,
                    },
                  }),
                );
              }}
              value={customFields.find(({ id }) => id === customField.id)?.plainTextValue}
              {...sharedProps}
            />
          );
        }
        case CustomFieldTypeName.date: {
          return (
            <ContentCollectionCustomFieldDateInput
              onSuccess={({ customFieldWithValues, date }) => {
                dispatch(
                  updateContentCollectionCustomFieldsInAllClipsAction({
                    customField: {
                      ...customFieldWithValues,
                      dateValue: date,
                    },
                  }),
                );

                dispatch(
                  setCustomFieldDateValueAction({
                    customField: {
                      ...customFieldWithValues,
                      dateValue: date,
                    },
                  }),
                );
              }}
              value={customFields.find(({ id }) => id === customField.id)?.dateValue}
              {...sharedProps}
            />
          );
        }
        default:
          return null;
      }
    },
    [customFields, clips, dispatch],
  );

  return (
    <ContentCollectionSidebar>
      <ContentCollectionSidebarHeader />
      {showCustomFields ? (
        <>
          <ContentCollectionSidebarSeparator />
          <ContentCollectionSidebarCustomFields renderCustomField={renderSidebarCustomField} />
        </>
      ) : isAboveMediumScreen ? (
        <>
          <ContentCollectionSidebarInstructions />
          <ContentCollectionSidebarTerms
            routes={{ terms: Routes.marketing.terms, privacy: Routes.marketing.privacy }}
          />
        </>
      ) : null}
    </ContentCollectionSidebar>
  );
};

ContentCollectionManageViewSidebar.displayName = 'ContentCollectionManageViewSidebar';
